import React                      from 'react';
import SaveIndicator              from '@/TokamapEditor/ux/SaveIndicator';
import leftSidebarStore           from '@/TokamapEditor/stores/leftSidebarStore';
import { observer }               from 'mobx-react-lite';
import { FormatPainterOutlined }  from '@ant-design/icons';
import { PictureOutlined }        from '@ant-design/icons';
import { BgColorsOutlined }       from '@ant-design/icons';
import View                       from '@widesk-ui/components/View';
import Alert                      from '@widesk-ui/components/Alert';
import { message }                from '@widesk-ui/hooks/useMessage';
import ColorButton                from '@/TokamapEditor/ux/AreaSidebar/ColorButton';
import EditableText               from '@widesk-ui/components/form/EditableText';
import SliderInputNumber          from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumber';
import { HEIGHT_RANGES }          from '@/TokamapEditor/helpers/mapKind';
import { STROKE_WIDTH_RANGES }    from '@/TokamapEditor/helpers/mapKind';
import { metersToUnit }           from '@/TokamapEditor/helpers/mapKind';
import { unitToMeters }           from '@/TokamapEditor/helpers/mapKind';
import currentEditorStore         from '@/TokamapEditor/stores/currentEditorStore';
import Segmented                  from '@widesk-ui/Segmented';
import SidebarTabs                from '@/TokamapEditor/ux/AreaSidebar/SidebarTabs';
import { NormalHoverScope }       from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import ThemePlaceTypesField       from '@/TokamapEditor/ux/ThemePlaceTypesField';
import ThemeContentSection        from '@/TokamapEditor/ux/ThemeContentSection';
import { ScopeToggle }            from '@/TokamapEditor/ux/ThemeFields';
import { ThemeSlider }            from '@/TokamapEditor/ux/ThemeFields';
import { ImposedField }           from '@/TokamapEditor/ux/ThemeFields';
import themeStore                 from '@/TokamapEditor/stores/themeStore';
import { Theme }                  from '@/TokamapEditor/stores/themeStore';
import { ThemeStyle }             from '@/TokamapEditor/stores/themeStore';
import useDesignTokens            from '@/hooks/useDesignTokens';
import Select                     from '@widesk-ui/components/form/Select';
import { zoomAppearanceOptions }  from '@/TokamapEditor/helpers/zoomAppearance';
import { DEFAULT_VOLUME_OPACITY } from '@tokamap/core';
import type { ThemeFieldTarget }  from '@/TokamapEditor/helpers/themeImposed';

type Section = 'appearance' | 'content';

const REVEAL_MAX_FRAMES = 30;

/** Le temps que le tiroir du thème finisse d'entrer : l'entourage se voit en entier. */
const REVEAL_DELAY_MS = 300;

const REVEAL_DURATION_MS = 2200;

/** Même valeur que `DEFAULT_BORDER_WIDTH` de `MapLibreAdapter` : ce que rend une bordure dont
 * l'épaisseur n'est pas renseignée. */
const DEFAULT_BORDER_WIDTH = 1;

/** Couleur de départ d'une couleur tout juste imposée : un gris neutre, à changer aussitôt. */
const IMPOSED_COLOR_START = '#9e9e9e';

/**
 * Contenu de la sous-sidebar d'un thème, en deux temps :
 *  - thème neuf (`themeId` absent) : on ne demande que le nom. Le valider enregistre, puis la
 *    sidebar bascule en modification sur le thème créé — sans fermeture/réouverture.
 *  - modification : nom + onglets d'apparence. Chaque changement enregistre.
 *
 * Les champs d'apparence réutilisent ceux de la sidebar d'une forme (`ColorButton`,
 * `NormalHoverToggle`, `Field`) pour que les deux écrans restent cohérents.
 *
 * Ce composant pose lui-même son padding (`openThemeForm` passe `padding: [0, 0]`) : les onglets
 * doivent aller d'un bord à l'autre, leur trait de séparation en dépend — même disposition que
 * `AreaSidebar`.
 */
/**
 * Nom du thème, modifiable en place dans le TITRE du tiroir (voir `openThemeForm`) plutôt que dans un
 * champ du corps : c'est déjà le titre affiché, un second endroit pour le même nom serait redondant.
 *
 * Lit le thème vivant dans le store, donc le titre suit le renommage sans que l'appelant ait à
 * repousser une nouvelle valeur dans les options du tiroir.
 */
export const ThemeTitle = observer(({ themeId }: { themeId: string; }) => {
	const theme = themeStore.getTheme(themeId);
	const tokens = useDesignTokens();

	if (!theme) return null;

	return (
		<View row centerV gap="xs">
			<BgColorsOutlined style={{ color: tokens.color.primary, flexShrink: 0 }} />

			<EditableText
				value={theme.name}
				placeholder="Nom du thème"
				errorMessage="Impossible de renommer le thème"
				onSubmit={async name => {
					// `EditableText` affiche le message de l'`Error` levée, sous le champ resté ouvert.
					if (!name) throw new Error('Le nom est obligatoire.');

					await themeStore.renameTheme(theme.id, name);
				}}
			/>
		</View>
	);
});

const ThemeForm = observer((props: { themeId: string; focus?: ThemeFieldTarget; }) => {
	// Relu dans le store à chaque rendu (et non reçu en prop) pour que le renommage s'y reflète.
	const theme = themeStore.getTheme(props.themeId);

	// Même indicateur que la fiche d'un type : cet écran n'a pas non plus de bouton d'enregistrement,
	// il faut donc que chaque modification dise qu'elle est partie. L'état vient du store, seul à
	// savoir quand une écriture commence et se termine (voir `themeStore._commit`).
	React.useEffect(() => {
		leftSidebarStore.setLowerOption('renderTopRight', () => <SaveIndicator state={themeStore.saveState} />);
	}, [themeStore.saveState]);

	// Thème supprimé depuis une autre sidebar/session pendant que ce formulaire était ouvert.
	if (!theme) {
		return (
			<View paddingV="sm" paddingH="md">
				<Alert type="warning" message="Ce thème n'existe plus." />
			</View>
		);
	}

	return <ThemeEditor theme={theme} focus={props.focus} />;
});

const ThemeEditor = observer(({ theme, focus }: { theme: Theme; focus?: ThemeFieldTarget; }) => {
	const [section, setSection] = React.useState<Section>(focus?.section ?? 'appearance');
	const [scope, setScope] = React.useState<NormalHoverScope>(focus?.section === 'appearance' ? (focus.scope ?? 'normal') : 'normal');
	const tokens = useDesignTokens();
	const container = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (focus) return revealThemeField(container, focus.field, tokens.color.primary);
	}, [focus]);

	const style = theme.appearance[scope];

	// Une modification part en requête immédiatement (le store revient à l'état précédent si elle
	// échoue) : c'est ici qu'on présente l'échec.
	// La liste des thèmes se réaligne d'elle-même sur le store (voir l'effet de `ThemeListSidebar`) :
	// rien à notifier ici, seule l'erreur est à présenter.
	const save = async (task: Promise<unknown>) => {
		try {
			await task;
		} catch (err) {
			message.exception(err);
		}
	};

	const setStyle = (patch: ThemeStyle) => save(themeStore.setThemeStyle(theme.id, scope, patch));

	return (
		<div ref={container}>
			<View p="md" style={{ borderBottom: `1px solid ${tokens.border.subtle}` }}>
				<ThemePlaceTypesField theme={theme} />
			</View>

			<SidebarTabs
				value={section}
				onChange={setSection}
				options={[
					{ value: 'appearance', label: 'Apparence', icon: <FormatPainterOutlined /> },
					{ value: 'content', label: 'Contenu', icon: <PictureOutlined /> },
				]}
			/>

			<View p="md">
				{section === 'content' ? (
					<ThemeContentSection theme={theme} save={save} focus={focus?.section === 'content' ? focus : undefined} />
				) : (
					<View gap="md">
						<ScopeToggle value={scope} onChange={setScope} />

						<ImposedField
							field="bgColor"
							label="Couleur de fond"
							imposed={style.bgColor !== undefined}
							onImpose={() => setStyle({ bgColor: IMPOSED_COLOR_START })}
							onRelease={() => setStyle({ bgColor: undefined })}
						>
							<ColorButton label="Fond" alpha value={style.bgColor} allowClear={false} onChange={bgColor => setStyle({ bgColor })} />
						</ImposedField>

						<ImposedField
							field="borderColor"
							label="Couleur de bordure"
							imposed={style.borderColor !== undefined}
							onImpose={() => setStyle({ borderColor: IMPOSED_COLOR_START })}
							onRelease={() => setStyle({ borderColor: undefined })}
						>
							<ColorButton label="Bordure" alpha value={style.borderColor} allowClear={false} onChange={borderColor => setStyle({ borderColor })} />
						</ImposedField>

						<ImposedField
							field="borderWidth"
							label="Épaisseur de bordure"
							imposed={style.borderWidth !== undefined}
							onImpose={() => setStyle({ borderWidth: DEFAULT_BORDER_WIDTH })}
							onRelease={() => setStyle({ borderWidth: undefined, borderWidthUnit: undefined })}
						>
							<ThemeBorderWidthField style={style} onSave={setStyle} />
						</ImposedField>

						<ImposedField
							field="borderDashed"
							label="Trait de la bordure"
							imposed={style.borderDashed !== undefined}
							onImpose={() => setStyle({ borderDashed: false })}
							onRelease={() => setStyle({ borderDashed: undefined })}
						>
							<Segmented
								block
								size="small"
								value={style.borderDashed ? 'dashed' : 'solid'}
								onChange={value => setStyle({ borderDashed: value === 'dashed' })}
								options={[
									{ value: 'solid', label: 'Plein' },
									{ value: 'dashed', label: 'Pointillés' },
								]}
							/>
						</ImposedField>

						<View h={1} bg={tokens.border.default} />

						{scope === 'hover' ? (
							<>
								<ImposedField
									field="hoverHeight"
									label="Hauteur"
									imposed={theme.hoverHeight !== undefined}
									onImpose={() => save(themeStore.setThemeHoverHeights(theme.id, { hoverHeight: 0 }))}
									onRelease={() => save(themeStore.setThemeHoverHeights(theme.id, { hoverHeight: undefined }))}
								>
									<ThemeMetersField
										value={theme.hoverHeight ?? 0}
										onSave={hoverHeight => save(themeStore.setThemeHoverHeights(theme.id, { hoverHeight }))}
									/>
								</ImposedField>

								<ImposedField
									field="hoverElevation"
									label="Hauteur au-dessus du sol"
									imposed={theme.hoverElevation !== undefined}
									onImpose={() => save(themeStore.setThemeHoverHeights(theme.id, { hoverElevation: 0 }))}
									onRelease={() => save(themeStore.setThemeHoverHeights(theme.id, { hoverElevation: undefined }))}
								>
									<ThemeMetersField
										value={theme.hoverElevation ?? 0}
										onSave={hoverElevation => save(themeStore.setThemeHoverHeights(theme.id, { hoverElevation }))}
									/>
								</ImposedField>

								<View size={12} color={tokens.text.muted}>
									Au survol, une valeur à zéro reprend celle de l'état normal.
								</View>
							</>
						) : (
							<>
								<ImposedField
									field="height"
									label="Hauteur"
									imposed={theme.height !== undefined}
									onImpose={() => save(themeStore.setThemeHeight(theme.id, 0))}
									onRelease={() => save(themeStore.setThemeHeight(theme.id, undefined))}
								>
									<ThemeMetersField
										value={theme.height ?? 0}
										onSave={height => save(themeStore.setThemeHeight(theme.id, height))}
									/>
								</ImposedField>

								<ImposedField
									field="elevation"
									label="Hauteur au-dessus du sol"
									imposed={theme.elevation !== undefined}
									onImpose={() => save(themeStore.setThemeElevation(theme.id, 0))}
									onRelease={() => save(themeStore.setThemeElevation(theme.id, undefined))}
								>
									<ThemeMetersField
										value={theme.elevation ?? 0}
										onSave={elevation => save(themeStore.setThemeElevation(theme.id, elevation))}
									/>
								</ImposedField>

								<ImposedField
									field="volumeOpacity"
									label="Opacité des murs"
									imposed={theme.volumeOpacity !== undefined}
									onImpose={() => save(themeStore.setThemeVolumeOpacity(theme.id, DEFAULT_VOLUME_OPACITY))}
									onRelease={() => save(themeStore.setThemeVolumeOpacity(theme.id, undefined))}
								>
									<ThemePercentField
										value={Math.round((theme.volumeOpacity ?? DEFAULT_VOLUME_OPACITY) * 100)}
										onSave={percent => save(themeStore.setThemeVolumeOpacity(theme.id, percent / 100))}
									/>
								</ImposedField>
							</>
						)}

						<View h={1} bg={tokens.border.default} />

						<ImposedField
							field="minZoomOffset"
							label="Apparition au zoom"
							imposed={theme.minZoomOffset !== undefined}
							onImpose={() => save(themeStore.setThemeMinZoomOffset(theme.id, 0))}
							onRelease={() => save(themeStore.setThemeMinZoomOffset(theme.id, undefined))}
						>
							<Select
								value={theme.minZoomOffset || ''}
								options={zoomAppearanceOptions(theme.minZoomOffset)}
								onChange={(value: unknown) => save(
									themeStore.setThemeMinZoomOffset(theme.id, typeof value === 'number' ? value : 0),
								)}
							/>
						</ImposedField>
					</View>
				)}
			</View>
		</div>
	);
});

/**
 * Fait défiler jusqu'au réglage puis l'entoure un instant. Le réglage peut n'apparaître qu'après
 * quelques rendus (onglet, état), et le tiroir finit d'entrer : on le cherche image par image.
 */
function revealThemeField(container: React.RefObject<HTMLElement>, field: string, color: string) {
	let frame = 0;
	let tries = 0;

	const seek = () => {
		const target = container.current?.querySelector<HTMLElement>(`[data-theme-field="${CSS.escape(field)}"]`);

		if (!target) {
			if (++tries < REVEAL_MAX_FRAMES) frame = requestAnimationFrame(seek);
			return;
		}

		target.scrollIntoView({ block: target.offsetHeight > window.innerHeight / 2 ? 'start' : 'center', behavior: 'smooth' });
		const hidden = `0 0 0 0 color-mix(in srgb, ${color} 0%, transparent), 0 0 0 0 color-mix(in srgb, ${color} 0%, transparent)`;
		const shown = `0 0 0 1.5px ${color}, 0 0 0 6px color-mix(in srgb, ${color} 14%, transparent)`;

		target.animate([
			{ boxShadow: hidden, offset: 0 },
			{ boxShadow: shown, offset: 0.12, easing: 'linear' },
			{ boxShadow: shown, offset: 0.6, easing: 'ease-in' },
			{ boxShadow: hidden, offset: 1 },
		], { duration: REVEAL_DURATION_MS, delay: REVEAL_DELAY_MS, easing: 'ease-out' });
	};

	frame = requestAnimationFrame(seek);

	return () => cancelAnimationFrame(frame);
}

/** Épaisseur de bordure du thème, en pixels à l'écran ou en longueur au sol, comme sur une forme. */
const ThemeBorderWidthField = observer(({ style, onSave }: {
	style: ThemeStyle;
	onSave: (patch: ThemeStyle) => Promise<void>;
}) => {
	const unit = currentEditorStore.lengthUnit;
	const inMeters = style.borderWidthUnit === 'm';

	return (
		<ThemeSlider
			value={inMeters ? metersToUnit(style.borderWidth ?? unitToMeters(1, unit), unit) : (style.borderWidth ?? DEFAULT_BORDER_WIDTH)}
			{...(inMeters ? STROKE_WIDTH_RANGES[unit] : { min: 1, max: 20, step: 1 })}
			unitSelect={{
				value: inMeters ? 'm' : 'px',
				options: [
					{ value: 'px', label: 'px' },
					{ value: 'm', label: unit },
				],
				title: `px : même épaisseur à l'écran, quel que soit le zoom. ${unit} : épaisseur au sol, qui suit le zoom.`,
				onChange: next => onSave(next === 'm'
					? { borderWidthUnit: 'm', borderWidth: unitToMeters(1, unit) }
					: { borderWidthUnit: undefined, borderWidth: 1 }),
			}}
			onSave={width => onSave({ borderWidth: inMeters ? unitToMeters(width, unit) : width })}
		/>
	);
});

/** Même principe que `ThemeBorderWidthField` : enregistrée au relâchement seulement. */
const ThemeMetersField = observer(({ value, onSave }: {
	value: number;
	onSave: (meters: number) => Promise<void>;
}) => {
	const [dragging, setDragging] = React.useState<number>();
	const unit = currentEditorStore.lengthUnit;
	const range = HEIGHT_RANGES[unit];

	return (
		<SliderInputNumber
			value={dragging ?? metersToUnit(value, unit)}
			min={0}
			max={range.sliderMax}
			inputMax={range.inputMax}
			step={range.step}
			suffix={unit}
			onChange={v => setDragging(Number(v) || 0)}
			onAfterChange={async v => {
				const input = Number(v) || 0;

				setDragging(input);
				await onSave(unitToMeters(input, unit));
				setDragging(undefined);
			}}
		/>
	);
});

const ThemePercentField = ({ value, onSave }: {
	value: number;
	onSave: (percent: number) => Promise<void>;
}) => {
	const [dragging, setDragging] = React.useState<number>();

	return (
		<SliderInputNumber
			value={dragging ?? value}
			min={0}
			max={100}
			step={10}
			suffix="%"
			onChange={v => setDragging(Number(v) || 0)}
			onAfterChange={async v => {
				const percent = Number(v) || 0;

				setDragging(percent);
				await onSave(percent);
				setDragging(undefined);
			}}
		/>
	);
};

export default ThemeForm;
